import { EmptyObject, Nullable } from '@crossbeam/types';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { MDM_PROPERTIES } from '@/constants/mdm';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import {
  PotentialRevenueCustomAmount,
  PotentialRevenueSettings,
} from '@/types/potential_revenue';
import { Source, SourceField } from '@/types/sources';

import { useBillingStore } from './BillingStore';
import { useFlashesStore } from './FlashesStore';
import { useSourcesStore } from './SourcesStore';

export type ProductTypeSettings = {
  sourceId: Nullable<number>;
  sourceFieldId: Nullable<number>;
};

export const usePotentialRevenueStore = defineStore('PotentialRevenue', () => {
  const potentialRevenueSettings = ref<PotentialRevenueSettings>({});
  const productTypeSettings = ref<ProductTypeSettings>({
    sourceId: null,
    sourceFieldId: null,
  });
  const billingStore = useBillingStore();
  const sourcesStore = useSourcesStore();
  const flashesStore = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      await Promise.all([billingStore.readySync, sourcesStore.readySync]);

      const productTypePromise = crossbeamApi.GET(
        '/v0.1/organization/settings/product-type',
      );

      const potentialRevenuePromise = crossbeamApi.GET(
        '/v0.1/organization/settings/potential-influence',
      );

      const [productTypeResponse, potentialRevenueResponse] = await Promise.all(
        [productTypePromise, potentialRevenuePromise],
      );

      if (potentialRevenueResponse?.data) {
        potentialRevenueSettings.value = potentialRevenueResponse.data;
      }

      if (productTypeResponse?.error) {
        flashesStore.addErrorFlash({
          message: 'Failed to load product settings',
        });
      } else if (productTypeResponse?.data) {
        productTypeSettings.value = {
          sourceId: productTypeResponse.data.source_id ?? null,
          sourceFieldId: productTypeResponse.data.source_field_id ?? null,
        };
      }
    },
  );

  refresh({ useCache: true });

  const allSettingsUnchecked = computed<boolean>(() => {
    if (billingStore.isFreeTier) return false;
    return (
      !potentialRevenueSettings.value.our_populations_potential_influence
        ?.length ||
      !potentialRevenueSettings.value.partner_populations_potential_influence
        ?.length
    );
  });
  const potentialRevenueColumnSettings = computed<PotentialRevenueCustomAmount>(
    () => {
      return (
        potentialRevenueSettings.value.amount_column_potential_influence || {}
      );
    },
  );

  /* TODO: When SourcesStore gets typed, replace these with Source and SourceField interfaces */
  const potentialRevenueSettingsSource = computed<
    Source | undefined | EmptyObject
  >(() => {
    // today we only let you configure a custom amount field for a single feed. If that is set in org settings, this will attempt to return the deal source for that feed
    const dealSources = sourcesStore.dealSources;
    if (!dealSources.length) return {};
    const sourceIds = Object.keys(potentialRevenueColumnSettings.value ?? {});
    if (sourceIds.length) {
      const piSourceIdStr = sourceIds[0]; // assuming only one source id for now, because that is all we support
      const source = dealSources.find(
        (source) => source.id === parseInt(piSourceIdStr || ''),
      );
      return source || {};
    }
    // if unable to find a source for a configured amount field, return the first deal source
    return dealSources[0];
  });
  const potentialRevenueSettingsSourceField = computed<
    SourceField | undefined | EmptyObject
  >(() => {
    if (!potentialRevenueSettingsSource.value) return;
    if (!Object.keys(potentialRevenueSettingsSource.value).length) return {};
    const sourceFieldIds = Object.values(
      potentialRevenueColumnSettings.value ?? {},
    );
    if (sourceFieldIds[0]) {
      const piSourceFieldId = Math.floor(sourceFieldIds[0]);
      return (
        potentialRevenueSettingsSource.value.fields?.find(
          (field) => field.id === piSourceFieldId,
        ) || {}
      );
    }
    return (
      potentialRevenueSettingsSource.value.fields?.find(
        (item) => item.mdm_property === MDM_PROPERTIES.deal.AMOUNT,
      ) || {}
    );
  });

  function setProductTypeSettings(sourceId: number, sourceFieldId: number) {
    productTypeSettings.value = { sourceId, sourceFieldId };
  }

  async function saveProductTypeSettings() {
    const { sourceId, sourceFieldId } = productTypeSettings.value;
    if (!sourceId || !sourceFieldId) return;

    const { error } = await crossbeamApi.PUT(
      '/v0.1/organization/settings/product-type',
      {
        body: {
          source_id: sourceId,
          source_field_id: sourceFieldId,
        },
      },
    );
    if (error) {
      captureException(error);
      throw error;
    }
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    allSettingsUnchecked,
    potentialRevenueColumnSettings,
    refreshPotentialRevenueStore: refresh,
    potentialRevenueSettings,
    potentialRevenueSettingsSource,
    potentialRevenueSettingsSourceField,
    productTypeSettings,
    setProductTypeSettings,
    saveProductTypeSettings,
  };
});
